/**
 * Pure helpers for the voice pipeline. Kept in isolated module so they are
 * unit-testable without jsdom + SpeechRecognition mocks.
 *
 * Contract — adaptive silence timeout:
 *   With `rec.continuous = true` (Chrome Web Speech API), the browser's own
 *   `isFinal` events are NOT used to commit the user's utterance. Instead,
 *   useJarvisVoice accumulates every interim+final chunk into a buffer and
 *   only commits when this timeout expires after the last `onresult`.
 *
 *   The timeout grows with how much the user has already said: short
 *   utterances commit quickly, long reflective ones get more patience.
 *
 *   timeout = base + min(words * 100, bonusMax)
 *
 *   Defaults map back to the env vars:
 *     base     = REACT_APP_VAD_SILENCE_BASE_MS    (default 2500ms)
 *     bonusMax = REACT_APP_VAD_SILENCE_BONUS_MAX_MS (default 1500ms)
 *
 *   Hard cap is 4000ms so a runaway counter never strands the pipeline.
 */
export const VAD_SILENCE_BASE_DEFAULT = 2500;
export const VAD_SILENCE_BONUS_MAX_DEFAULT = 1500;
export const VAD_SILENCE_HARD_CAP = 4000;

const _envInt = (key, fallback, min, max) => {
  if (typeof process === "undefined" || !process.env) return fallback;
  const raw = process.env[key];
  if (raw === undefined || raw === null || raw === "") return fallback;
  const v = parseInt(raw, 10);
  if (!Number.isFinite(v)) return fallback;
  if (v < min || v > max) return fallback;
  return v;
};

export const readVadSilenceBaseFromEnv = () =>
  _envInt("REACT_APP_VAD_SILENCE_BASE_MS", VAD_SILENCE_BASE_DEFAULT, 500, 10000);

export const readVadSilenceBonusMaxFromEnv = () =>
  _envInt("REACT_APP_VAD_SILENCE_BONUS_MAX_MS", VAD_SILENCE_BONUS_MAX_DEFAULT, 0, 5000);

/**
 * Compute adaptive silence timeout given how many words the user has said
 * in the current utterance so far.
 *
 * @param {number} wordCount  words already accumulated (>=0)
 * @param {number} [base]     base ms before bonus (default from env)
 * @param {number} [bonusMax] max ms of bonus added on top (default from env)
 * @returns {number} timeout in ms, clipped to VAD_SILENCE_HARD_CAP
 */
export function computeAdaptiveSilenceTimeout(wordCount, base, bonusMax) {
  const _base = Number.isFinite(base) ? base : readVadSilenceBaseFromEnv();
  const _bonusMax = Number.isFinite(bonusMax) ? bonusMax : readVadSilenceBonusMaxFromEnv();
  const w = Math.max(0, Math.floor(Number(wordCount) || 0));
  const bonus = Math.min(w * 100, _bonusMax);
  const total = _base + bonus;
  return Math.min(total, VAD_SILENCE_HARD_CAP);
}

/**
 * VOICE-C1 F2 — Tiered (non-linear) silence timeout.
 *
 * V2's linear `bonus = min(w*100, 1500)` saturated at 15 words, meaning a
 * pausada-mas-longa frase ("agenda uma reunião amanhã às três com o João
 * pra discutir o projeto Marrocos da Beeit") still capped at 4000ms — too
 * little patience for 18+ word utterances with natural micro-pauses.
 *
 * Tiered bonus rewards each tier slightly more generously:
 *   w in [0, 5]   → bonus = w * 100        (linear, same as V2)
 *   w in (5, 10]  → bonus = 500 + (w-5) * 150
 *   w in (10, 20] → bonus = 1250 + (w-10) * 200
 *   w > 20        → bonus = 3250 + (w-20) * 100  (slower growth past 20)
 *
 * Bonus is capped at VAD_SILENCE_TIERED_BONUS_CAP (5000ms) so a runaway
 * 50+ word utterance never strands the pipeline. Total is capped at
 * VAD_SILENCE_TIERED_HARD_CAP (7500ms).
 *
 * Examples (base 2500):
 *    0w → 2500   (base only)
 *    5w → 3000   (V2 same)
 *   10w → 3750   (V2: 3500)
 *   15w → 4750   (V2 capped at 4000)
 *   20w → 5750   (V2 capped at 4000)
 *   25w → 6250
 *   40w → 7500   (hits absolute hard cap)
 */
export const VAD_SILENCE_TIERED_BONUS_CAP = 5000;
export const VAD_SILENCE_TIERED_HARD_CAP = 7500;

export function computeTieredSilenceTimeout(wordCount, base) {
  const _base = Number.isFinite(base) ? base : readVadSilenceBaseFromEnv();
  const w = Math.max(0, Math.floor(Number(wordCount) || 0));
  let bonus;
  if (w <= 5) bonus = w * 100;
  else if (w <= 10) bonus = 500 + (w - 5) * 150;
  else if (w <= 20) bonus = 1250 + (w - 10) * 200;
  else bonus = 3250 + (w - 20) * 100;
  bonus = Math.min(bonus, VAD_SILENCE_TIERED_BONUS_CAP);
  return Math.min(_base + bonus, VAD_SILENCE_TIERED_HARD_CAP);
}

/**
 * Count whitespace-separated tokens in a string. Handles undefined/null,
 * collapses repeated spaces, ignores empty strings.
 */
export function countWords(text) {
  if (!text) return 0;
  const s = String(text).trim();
  if (!s) return 0;
  return s.split(/\s+/).length;
}

/**
 * Find the first complete sentence in a streaming buffer for early TTS
 * dispatch (pre-fetch).
 *
 * Match rules (TTS-PRONUNCIA-FIX-4):
 *   - Strong terminator only: . ! ?  (no commas — "Mestre, em que..."
 *     must dispatch as one TTS, not two)
 *   - Strategy 1 (high confidence): terminator + ACTUAL whitespace.
 *     Whitespace confirms the sentence ended; no chunk-boundary ambiguity.
 *   - Strategy 2 (single-sentence recovery): terminator at end-of-string
 *     IF unambiguous. The "." case is the dangerous one — could be a
 *     thousands separator (mid-number) or sentence end. We allow EOS only
 *     when:
 *       a) terminator is "!" or "?" (never used as thousands sep), OR
 *       b) terminator is "." preceded by a NON-DIGIT (clear word end), OR
 *       c) terminator is "." preceded by ",\d{2}" (BR money decimal end —
 *          "R$ 76.945,00." pattern, where the "00." after the comma
 *          unambiguously marks sentence end after a money value).
 *     Anything else with "." at EOS (e.g., chunk ending "R$ 76.") is
 *     rejected — wait for next event with confirming whitespace.
 *
 *   - Trimmed sentence must meet `minChars` floor (default 12) so we
 *     don't pay TTS round-trip for "Pronto!" / "Tá bem!" trivia.
 *
 * Returns: { sentence: string, endIndex: number } when a sentence is
 * ready to dispatch; null otherwise. `endIndex` is the position AFTER
 * the consumed terminator+whitespace — caller uses it to slice the
 * remainder for queueing on `done`.
 */
export function pickFirstSentenceForPrefetch(accumulated, minChars = 12) {
  if (!accumulated) return null;

  // Strategy 1 — terminator + real whitespace.
  let m = accumulated.match(/^([\s\S]*?[.!?])\s/);
  if (m && m[1].trim().length >= minChars) {
    return { sentence: m[1], endIndex: m[0].length };
  }

  // Strategy 2 — terminator at end-of-string with disambiguation.
  m = accumulated.match(/^([\s\S]*?[.!?])$/);
  if (m && m[1].trim().length >= minChars) {
    const sentence = m[1];
    const last = sentence.slice(-1);
    // "!" and "?" are never thousands separators — always safe.
    if (last === "!" || last === "?") {
      return { sentence, endIndex: m[0].length };
    }
    // "." needs disambiguation. Look at what precedes the terminator.
    if (last === ".") {
      const before = sentence.slice(-2, -1);
      // Non-digit before the dot ("sucesso.", "ajudar.") = clear word end.
      if (before && !/[0-9]/.test(before)) {
        return { sentence, endIndex: m[0].length };
      }
      // ",\d{2}\." = BR money decimal end ("R$ 76.945,00.").
      if (/,\d{2}\.$/.test(sentence)) {
        return { sentence, endIndex: m[0].length };
      }
      // Anything else (e.g., "R$ 76." mid-number) — wait for confirming \s.
    }
  }

  return null;
}

/**
 * Append a chunk to an existing accumulator buffer, normalising whitespace
 * so we never produce double spaces or stray newlines after concat.
 */
export function appendToAccumulator(buffer, chunk) {
  if (!chunk) return buffer || "";
  const trimmed = String(chunk).trim();
  if (!trimmed) return buffer || "";
  if (!buffer) return trimmed;
  return `${buffer} ${trimmed}`.replace(/\s+/g, " ");
}

/**
 * VOICE-UNIFICAR — TTS echo detection.
 *
 * Web Speech API STT runs in parallel to the MediaStream we hardware-mute
 * via track.enabled=false. The STT engine, however, captures audio via the
 * OS mic — speaker bleed (TTS audio playing through the laptop speakers
 * looping back through the mic) gets transcribed even when our track is
 * muted. Symptom: user asks "resumo do mês", Nexo replies, and fragments
 * of Nexo's own reply show up in the message panel as if the user said
 * them ("Que venceu a", "resumo financeiro receitas", etc).
 *
 * Defense: track every chunk of text we send to TTS in a rolling buffer.
 * Before committing a user transcript, check if it appears as a substring
 * of (or is heavily contained in) the recent TTS buffer — if so, drop it
 * as echo.
 *
 * Conservative-by-design: only drops short fragments that are CLEAR
 * substrings, so a legit user question that happens to repeat a word or
 * two from Nexo's reply still goes through.
 */

// Normalise a string for echo comparison — lowercase, strip accents,
// collapse non-letter chars to single spaces, trim. So "que venceu a" and
// "Que venceu a." both normalise to "que venceu a".
export function normalizeForEcho(text) {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Returns true if `transcript` looks like an echo of `ttsBuffer`.
 *
 * Heuristics (any one triggers):
 *   1. Transcript is short (<= 6 words) AND is a substring of buffer.
 *   2. Word-overlap fraction (transcript words found in buffer) is >=
 *      ECHO_OVERLAP_THRESHOLD.
 *
 * Echo de TTS chega em fragmentos acumulados (varios onresult durante a
 * fala do Nexo) e pode somar 13+ palavras quando emendados — nao da pra
 * cortar por tamanho. Em compensacao, overlap alto (>=75%) com TTS
 * recente e quase sempre eco: pergunta legitima do user dificilmente
 * repete 75% das palavras da resposta anterior.
 */
export const ECHO_OVERLAP_THRESHOLD = 0.75;

// REFINO-pos-a05eb3a (2026-06-02) — Allowlist de respostas de
// clarificação de scope. Frases-resposta legítimas pras perguntas que o
// Nexo faz ("quer ver só o que falta ou a semana inteira?"). Por
// construção repetem palavras da pergunta — looksLikeTtsEcho dispara
// false positive. Bypass nos filtros (quiet + echo) quando transcript
// normaliza pra uma delas.
// Lista vem de services/summary_day_planner._scope_options().
export const SCOPE_ANSWER_ALLOWLIST = [
  "tudo", "tudo do dia", "só o que falta", "so o que falta",
  "a semana inteira", "semana inteira",
  "só o que falta da semana", "so o que falta da semana",
  "o mês inteiro", "o mes inteiro", "mês inteiro", "mes inteiro",
  "só o que falta do mês", "so o que falta do mes",
  "falta",
];

function _normalizeForScopeAllowlist(raw) {
  return (raw || "")
    .toLowerCase()
    .normalize("NFD")
    // strip combining diacritical marks U+0300..U+036F
    .replace(/[̀-ͯ]/g, "")
    .replace(/[.,!?]/g, "")
    .trim();
}

export function isScopeAnswerAllowlisted(raw) {
  const norm = _normalizeForScopeAllowlist(raw);
  if (!norm) return false;
  return SCOPE_ANSWER_ALLOWLIST.some(
    (phrase) => norm === _normalizeForScopeAllowlist(phrase),
  );
}

export function looksLikeTtsEcho(transcript, ttsBuffer) {
  const t = normalizeForEcho(transcript);
  const b = normalizeForEcho(ttsBuffer);
  if (!t || !b) return false;
  const tWords = t.split(" ").filter(Boolean);
  if (tWords.length === 0) return false;

  // Rule 1: clear substring (short echo of phrasing in TTS)
  if (tWords.length <= 6 && b.includes(t)) return true;

  // Rule 2: word-overlap fraction — qualquer tamanho de frase
  const bWords = new Set(b.split(" ").filter(Boolean));
  let hits = 0;
  for (const w of tWords) if (bWords.has(w)) hits += 1;
  const overlap = hits / tWords.length;
  return overlap >= ECHO_OVERLAP_THRESHOLD;
}

/**
 * VOICE-C1 F1 — Adaptive thresholds from a measured noise floor.
 *
 * Given a baseline RMS sample captured during 1s of silence at mount, derive
 * thresholds that scale with the user's environment. Quiet office gets the
 * standard V2 thresholds (because the floor is well below the floor
 * multiplier); noisy café gets bumped numbers so background hum doesn't
 * trigger VAD-start or barge-in false positives.
 *
 * Floors are kept identical to V2 defaults so the worst case (very quiet
 * room with floor ~ 0) lands on the same numbers as V2.
 *
 *   rmsStart_dyn   = max(0.012, floor * 2.5)
 *   rmsBargeIn_dyn = max(0.030, floor * 5.0)
 *
 * Both are also capped at sensible upper bounds (0.10 start, 0.20 barge-in)
 * so a wildly noisy environment doesn't lift them past the point where
 * even loud speech would not trigger.
 */
export const NOISE_FLOOR_RMS_START_MIN = 0.012;
export const NOISE_FLOOR_RMS_START_MAX = 0.10;
export const NOISE_FLOOR_RMS_BARGEIN_MIN = 0.030;
export const NOISE_FLOOR_RMS_BARGEIN_MAX = 0.20;

export function computeDynamicThresholds(noiseFloor) {
  const floor = Math.max(0, Number(noiseFloor) || 0);
  const rmsStart = Math.min(
    NOISE_FLOOR_RMS_START_MAX,
    Math.max(NOISE_FLOOR_RMS_START_MIN, floor * 2.5),
  );
  const rmsBargeIn = Math.min(
    NOISE_FLOOR_RMS_BARGEIN_MAX,
    Math.max(NOISE_FLOOR_RMS_BARGEIN_MIN, floor * 5.0),
  );
  return { rmsStart, rmsBargeIn, noiseFloor: floor };
}

/**
 * VOICE-C1 F4 — Pick best-guess output device class.
 *
 * Cascade (most reliable first):
 *   1. enumerateDevices.label has useful info → headset/speaker
 *      detection via label regex (existing V2 logic).
 *   2. iOS Safari (UA match) → assume speaker. Safari blocks output device
 *      labels even after permission, so step 1 returns empty → without
 *      this fallback we'd default to "speaker" anyway, but explicit
 *      intent is documented + testable.
 *   3. Other mobile (Android) → assume speaker (notebooks are the only
 *      common headphone case among non-mobile).
 *   4. Desktop with no labels → assume speaker (V2 default — safer
 *      threshold; reduces echo false-starts on built-in mics).
 *
 * Returns "speaker" | "headphone".
 *
 * Inputs are explicit (not read from globals) so this is unit-testable
 * without touching the DOM / navigator.
 */
export function pickOutputDeviceClass({ outputs, userAgent } = {}) {
  // 1) Label-based detection (V2 path).
  if (Array.isArray(outputs) && outputs.length > 0) {
    const hasUsefulLabels = outputs.some((d) => d && (d.label || "").length > 0);
    if (hasUsefulLabels) {
      const looksLikeHeadphone = outputs.some((d) => {
        const lbl = ((d && d.label) || "").toLowerCase();
        return /headphone|headset|earphone|earbud|airpods|fone|bluetooth/.test(lbl);
      });
      return looksLikeHeadphone ? "headphone" : "speaker";
    }
  }

  // 2) iOS Safari — labels blocked, assume speaker.
  const ua = (userAgent || "").toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (/macintosh/.test(ua) && /version\/[\d.]+/.test(ua) && /mobile/.test(ua));
  const isSafari = /safari/.test(ua) && !/chrome|crios|fxios|edgios/.test(ua);
  if (isIOS && isSafari) return "speaker";

  // 3) Other mobile platforms (Android, generic mobile UA).
  if (/android|mobile|tablet/.test(ua)) return "speaker";

  // 4) Desktop with no info → conservative default (speaker threshold).
  return "speaker";
}
